import React, { useMemo, useState } from 'react'
import { isNil } from 'lodash'
import { IconArrowUp, IconArrowDown, IconArrowsSort, IconDownload } from '@tabler/icons-react'

import { Accordion } from '@/components/accordions/Accordion'
import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { Table } from '@/components/tables/Table/Table'
import { exportCsv } from '@/components/helpers/exportToCsv'
import { parseToCsv } from '@/components/helpers/parseToCsv'

// Query-engine tables with more rows than this render collapsed by default (they eat
// vertical space); smaller ones stay open so a quick comparison isn't hidden behind a click.
const COLLAPSE_ABOVE_ROWS = 5

const SortIcon = ({ col, sortField, sortDir }) => {
  if (sortField !== col) {
    return <IconArrowsSort size={12} className="cowork-table-message__sort-icon" />
  }

  return sortDir === 'asc' ? (
    <IconArrowUp
      size={12}
      className="cowork-table-message__sort-icon cowork-table-message__sort-icon--active"
    />
  ) : (
    <IconArrowDown
      size={12}
      className="cowork-table-message__sort-icon cowork-table-message__sort-icon--active"
    />
  )
}

export const KodiTableMessage = ({ message }) => {
  const [sortField, setSortField] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  const tableData = useMemo(() => {
    try {
      const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data

      if (!data || !data.columns || !data.rows) {
        return null
      }

      return data
    } catch {
      return null
    }
  }, [message.data])

  const [expanded, setExpanded] = useState((tableData?.rows?.length ?? 0) <= COLLAPSE_ABOVE_ROWS)

  // Resolve a cell value from a row by column name. Rows are objects keyed by
  // field name, or plain arrays.
  const getRowValue = (row, colIndex, colName) => {
    if (Array.isArray(row)) {
      return colIndex < row.length ? row[colIndex] : ''
    }

    if (isNil(row) || typeof row !== 'object') {
      return ''
    }

    if (colName in row) {
      return row[colName]
    }

    // Match a column label to a key ignoring case/punctuation ("CALL_ID" -> "call_id").
    const normalizedCol = colName.toLowerCase().replace(/[^a-z0-9]/g, '_')
    const matchKey = Object.keys(row).find(
      (columnKey) => columnKey.toLowerCase().replace(/[^a-z0-9]/g, '_') === normalizedCol
    )

    // No matching key -> empty cell. Never positionally backfill with the Nth
    // key: that fills a phantom column (e.g. a call_id column the plan never
    // projected) with an unrelated value like the extracted_data blob.
    return matchKey ? row[matchKey] : ''
  }

  const sortedRows = useMemo(() => {
    if (!tableData || !sortField) {
      return tableData?.rows || []
    }

    const rows = [...tableData.rows]
    const colIndex = tableData.columns.indexOf(sortField)

    rows.sort((rowA, rowB) => {
      const rowAVal = getRowValue(rowA, colIndex, sortField)
      const rowBVal = getRowValue(rowB, colIndex, sortField)

      if (rowAVal == null && rowBVal == null) {
        return 0
      }
      if (rowAVal == null) {
        return 1
      }
      if (rowBVal == null) {
        return -1
      }

      if (typeof rowAVal === 'number' && typeof rowBVal === 'number') {
        return sortDir === 'asc' ? rowAVal - rowBVal : rowBVal - rowAVal
      }

      const strA = String(rowAVal)
      const strB = String(rowBVal)

      return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA)
    })

    return rows
  }, [tableData, sortField, sortDir])

  if (!tableData) {
    return (
      <div className="cowork-table-message cowork-table-message--error">
        Table could not be rendered.
      </div>
    )
  }

  const handleSort = (col) => {
    if (sortField === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(col)
      setSortDir('asc')
    }
  }

  const formatValue = (val) => {
    if (val == null) {
      return '—'
    }
    if (typeof val === 'number') {
      return Number.isInteger(val) ? val : val.toFixed(1)
    }
    // Nested objects/arrays (e.g. a raw extracted_data blob) would otherwise
    // stringify to "[object Object]" — show readable JSON instead.
    if (typeof val === 'object') {
      return JSON.stringify(val)
    }

    return String(val)
  }

  const handleDownloadCsv = (event) => {
    // The button lives in the accordion header; don't also toggle it.
    event.stopPropagation()

    // Keyed by index so duplicate column names (joins) don't overwrite each other.
    const headers = tableData.columns.map((col, colIdx) => ({
      accessor: String(colIdx),
      label: col,
    }))
    const data = sortedRows.map((row) =>
      Object.fromEntries(
        tableData.columns.map((col, colIdx) => [String(colIdx), getRowValue(row, colIdx, col)])
      )
    )

    exportCsv(parseToCsv({ data, headers }), null, tableData.title || 'Table')
  }

  const rowCount = sortedRows.length
  const accordionTitle = `${tableData.title || 'Table'} · ${rowCount} ${
    rowCount === 1 ? 'row' : 'rows'
  }`

  return (
    <div className="cowork-table-message">
      <Accordion
        styled
        id={`cowork-table-${tableData.title || 'result'}`}
        title={accordionTitle}
        // A chevron alone reads as decoration on the white transcript; naming the
        // action is what tells the reader the header opens.
        actions={
          <>
            <Button
              small
              square
              icon={<IconDownload stroke={1.5} />}
              aria-label="Download CSV"
              data-testid="kodi-table-download-csv"
              onClick={handleDownloadCsv}
              // Enter/Space on the button would otherwise reach the header's toggle handler.
              onKeyDown={(event) => event.stopPropagation()}
            />
            <Pill brand small>
              {expanded ? 'Hide' : 'Show'}
            </Pill>
          </>
        }
        expanded={expanded}
        handleToggle={() => setExpanded((prev) => !prev)}
      >
        <div className="cowork-table-message__scroll">
          <Table compact>
            <thead>
              <tr>
                {tableData.columns.map((col) => (
                  <th
                    key={col}
                    className="cowork-table-message__th"
                    onClick={() => handleSort(col)}
                  >
                    <span className="cowork-table-message__th-content">
                      {col}
                      <SortIcon col={col} sortField={sortField} sortDir={sortDir} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, rowIdx) => {
                const rowKey = `${tableData.columns
                  .slice(0, 2)
                  .map((col, colIdx) => String(getRowValue(row, colIdx, col) ?? ''))
                  .join('|')}|${rowIdx}`

                return (
                  <tr key={rowKey}>
                    {tableData.columns.map((col, colIdx) => (
                      <td key={`${rowKey}-${col}`}>{formatValue(getRowValue(row, colIdx, col))}</td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </Accordion>
    </div>
  )
}
